import { OrgRole, type OrganizationMembership } from '../types/OrganizationMembership.js'

/** Denormalized `organizationIds` on User — kept in sync with `organizations[]` for Firestore `array-contains` queries. */
export function organizationIdsFromMemberships(organizations: OrganizationMembership[] | undefined): string[] {
  const ids = (organizations ?? []).map((m) => m.organizationId)
  return [...new Set(ids)]
}

// Field bag for any membership write — derives organizationIds so it can't drift from organizations.
// Use this instead of hand-writing organizationIds. activeOrganizationId is included only when provided.
export function buildMembershipFields(
  organizations: OrganizationMembership[],
  activeOrganizationId?: string
): { organizations: OrganizationMembership[]; organizationIds: string[]; activeOrganizationId?: string } {
  return {
    organizations,
    organizationIds: organizationIdsFromMemberships(organizations),
    ...(activeOrganizationId !== undefined ? { activeOrganizationId } : {})
  }
}

/** Roles that play and live in the gamer shell. A leader is a participant with extra coaching and data. */
export const PARTICIPANT_ORG_ROLES: OrgRole[] = [OrgRole.GAMER, OrgRole.LEADER]

/** Roles that run sprints and live in the facilitator shell. */
export const STAFF_ORG_ROLES: OrgRole[] = [OrgRole.FACILITATOR, OrgRole.ADMIN]

export const isParticipantOrgRole = (role?: OrgRole): boolean => !!role && PARTICIPANT_ORG_ROLES.includes(role)

export const isStaffOrgRole = (role?: OrgRole): boolean => !!role && STAFF_ORG_ROLES.includes(role)

export const isOrgRole = (value: unknown): value is OrgRole => Object.values(OrgRole).includes(value as OrgRole)

/** The roles someone can be given, in the order listed: the leader role only where it is offered. */
export const getAssignableOrgRoles = (roles: OrgRole[], isLeaderRoleOffered: boolean): OrgRole[] =>
  isLeaderRoleOffered ? roles : roles.filter((role) => role !== OrgRole.LEADER)
